import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { shiftsService } from '../services/shiftsService';
import type {
  ShiftResponse,
  ShiftQuery,
  ShiftListResponse,
  CreateShiftRequest,
  UpdateShiftRequest,
} from '@troia-v3/types';

/**
 * Hook para listar turnos com filtros e paginação
 */
export const useShifts = (query?: ShiftQuery) => {
  return useQuery<ShiftListResponse>({
    queryKey: ['shifts', query],
    queryFn: () => shiftsService.getShifts(query),
  });
};

/**
 * Hook para obter um turno específico por ID
 */
export const useShift = (id: string | undefined) => {
  return useQuery<ShiftResponse>({
    queryKey: ['shift', id],
    queryFn: () => shiftsService.getShiftById(id!),
    enabled: !!id,
  });
};

/**
 * Hook para criar um novo turno
 */
export const useCreateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShiftRequest) => shiftsService.createShift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-dropdown'] });
    },
  });
};

/**
 * Hook para atualizar um turno existente
 */
export const useUpdateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShiftRequest }) =>
      shiftsService.updateShift(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shift', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['shifts-dropdown'] });
    },
  });
};

/**
 * Hook para excluir um turno
 */
export const useDeleteShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shiftsService.deleteShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-dropdown'] });
    },
  });
};

/**
 * Hook para duplicar um turno (clona config; membros não são copiados)
 */
export const useDuplicateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shiftsService.duplicateShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-dropdown'] });
      toast.success('Turno duplicado com sucesso.');
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.message || 'Erro ao duplicar turno'),
  });
};

/**
 * Hook para ativar um turno
 */
export const useActivateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shiftsService.activateShift(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shift', id] });
    },
  });
};

/**
 * Hook para desativar um turno
 */
export const useDeactivateShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shiftsService.deactivateShift(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shift', id] });
    },
  });
};

/**
 * Hook para listar turnos para dropdown (dados mínimos)
 */
export const useShiftsDropdown = () => {
  return useQuery<Array<{ id: string; name: string; description?: string }>>({
    queryKey: ['shifts-dropdown'],
    queryFn: () => shiftsService.getShiftsForDropdown(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obter turnos por equipe
 */
export const useShiftsByTeam = (teamId: string | undefined) => {
  return useQuery<ShiftResponse[]>({
    queryKey: ['shifts', 'team', teamId],
    queryFn: () => shiftsService.getShiftsByTeam(teamId!),
    enabled: !!teamId,
  });
};

/**
 * Hook para obter turnos de um usuário
 */
export const useUserShifts = (userId: string | undefined) => {
  return useQuery<ShiftResponse[]>({
    queryKey: ['shifts', 'user', userId],
    queryFn: () => shiftsService.getUserShifts(userId!),
    enabled: !!userId,
  });
};
